import React from 'react';
import { Progress } from '@/components/ui/progress';
import { Card } from '@/components/ui/card';
import { WIZARD_STEPS } from './types';
import { useUploadWizard } from './hooks/useUploadWizard';
import FileUploadStep from './steps/FileUploadStep';
import MetadataStep from './steps/MetadataStep';
import SpotifyIntegrationStep from './steps/SpotifyIntegrationStep';
import PricingStep from './steps/PricingStep';
import ReviewStep from './steps/ReviewStep';

interface SequenceUploadWizardProps {
  onSuccess?: (sequenceId: string) => void;
  onCancel?: () => void;
}

const SequenceUploadWizard: React.FC<SequenceUploadWizardProps> = ({ 
  onSuccess, 
  onCancel 
}) => {
  const wizard = useUploadWizard(onSuccess, onCancel);
  
  const progressPercentage = ((wizard.currentStep + 1) / wizard.totalSteps) * 100;

  const renderCurrentStep = () => {
    switch (wizard.getCurrentStepId()) {
      case 'file':
        return (
          <FileUploadStep
            uploadedFile={wizard.uploadedFile}
            onFileSelect={wizard.handleFileSelect}
            onFileRemove={wizard.handleFileRemove}
            onNext={wizard.goNext}
            uploadProgress={wizard.uploadProgress}
            isUploading={wizard.isUploading}
          />
        );
      
      case 'metadata':
        return (
          <MetadataStep
            form={wizard.form}
            onNext={wizard.goNext}
            onBack={wizard.goBack}
          />
        );
      
      case 'spotify':
        return (
          <SpotifyIntegrationStep
            form={wizard.form}
            selectedTrack={wizard.selectedSpotifyTrack}
            onTrackSelect={wizard.handleSpotifyTrackSelect}
            onNext={wizard.goNext}
            onBack={wizard.goBack}
            onSkip={wizard.goNext}
          />
        );
      
      case 'pricing':
        return (
          <PricingStep
            form={wizard.form}
            onNext={wizard.goNext}
            onBack={wizard.goBack}
          />
        );
      
      case 'review':
        return (
          <ReviewStep
            form={wizard.form}
            uploadedFile={wizard.uploadedFile}
            selectedTrack={wizard.selectedSpotifyTrack}
            onSubmit={wizard.handleSubmit}
            onBack={wizard.goBack}
            isSubmitting={wizard.isSubmitting}
          />
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Progress Header */}
      <Card className="p-6">
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">Upload New Sequence</h2>
              <p className="text-sm text-gray-600 mt-1">
                Step {wizard.currentStep + 1} of {wizard.totalSteps}: {wizard.currentStepInfo.label}
              </p>
            </div>
            {onCancel && (
              <button
                onClick={onCancel}
                className="text-gray-500 hover:text-gray-700 text-sm"
              >
                Cancel
              </button>
            )}
          </div>
          
          <Progress value={progressPercentage} className="h-2" />
          
          {/* Step Indicators */}
          <div className="flex justify-between">
            {WIZARD_STEPS.map((step, index) => (
              <div
                key={step.id}
                className={`flex flex-col items-center cursor-pointer transition-colors ${
                  index === wizard.currentStep
                    ? 'text-primary'
                    : index < wizard.currentStep
                    ? 'text-green-600'
                    : 'text-gray-400'
                }`}
                onClick={() => {
                  // Only allow going back to previous steps
                  if (index < wizard.currentStep) {
                    wizard.goToStep(index);
                  }
                }}
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium border-2 transition-colors ${
                    index === wizard.currentStep
                      ? 'border-primary bg-primary text-white'
                      : index < wizard.currentStep
                      ? 'border-green-600 bg-green-600 text-white'
                      : 'border-gray-300 bg-white'
                  }`}
                >
                  {index < wizard.currentStep ? '✓' : index + 1}
                </div>
                <span className="text-xs mt-1 hidden sm:block">{step.label}</span>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* Current Step Content */}
      <div className="animate-in fade-in-0 slide-in-from-bottom-4 duration-300">
        {renderCurrentStep()}
      </div>
    </div>
  );
};

export default SequenceUploadWizard;